import type { ReactNode } from 'react';

import { Button, Card, Input, PhoneInput } from '@parrot-software/design-system';
import { cn } from '@parrot-software/utils';
import { Controller } from 'react-hook-form-v7';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import type { AccountRequirements } from '@identity/domain/types';
import { FieldLabel, VerificationBadge } from '@identity/presentation/components/base';
import { PROFILE_VERIFY_PHONE_ROUTE } from '@identity/presentation/constants';
import { useProfileContactForm } from '@identity/presentation/hooks';

/** What the verification screen opens with, and what never travels in the URL: a phone in an address
 * bar stays. */
export type PhoneVerificationHandoff = {
  phone: string;
};

export type ProfileContactCardProps = {
  /** Rendered between this card and the footer, so the submit closes the screen and not one card. */
  children?: ReactNode;
  email: string;
  /** The phone the account has registered, in E.164. Absent means it has none. */
  phone?: string;
  requirements: AccountRequirements;
};

/**
 * The contact data of Mi perfil: the email as a fact, the phone as the one thing its owner can
 * change here, and a badge for each.
 *
 * There is one badge per value and it lives in one place in the markup. From `md` up it sits beside
 * the input, which stops at a readable width to leave room for it; on a phone it wraps up next to the
 * label instead, where the input needs the whole line. The note always closes the field full width.
 *
 * Nothing is sent from here. The button hands the number over to the verification screen, which is
 * where the channel is chosen and the link asked for, so a refusal lands next to the button that
 * caused it.
 *
 * @param {ProfileContactCardProps} props - Component properties
 */
export const ProfileContactCard = ({ children, email, phone, requirements }: ProfileContactCardProps) => {
  const { t } = useTranslation('identity');
  const history = useHistory();
  const { form, phoneBadge, isSubmitDisabled } = useProfileContactForm({
    isPhoneVerified: requirements.PHONE,
    registeredPhone: phone,
  });

  const {
    clearErrors,
    control,
    formState: { errors },
    handleSubmit,
  } = form;
  const fieldErrorKey = errors.phone?.message;

  // Replaces the entry instead of stacking one: going back to a verification already under way
  // would land on a screen that lost the number it was started with.
  const onSubmit = handleSubmit((values) => {
    const handoff: PhoneVerificationHandoff = { phone: values.phone };
    history.replace(PROFILE_VERIFY_PHONE_ROUTE, handoff);
  });

  return (
    <form className="flex flex-col gap-6" noValidate onSubmit={onSubmit}>
      <Card
        className="flex w-full flex-col gap-6 border-0 bg-transparent p-0 font-sans shadow-none md:border md:bg-card md:p-6 md:shadow-xs"
        data-testid="profile-contact-card"
      >
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-semibold text-foreground">{t('profile.contact.title')}</h2>
          <p className="text-sm text-muted-foreground">{t('profile.contact.description')}</p>
        </div>

        <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
          <FieldLabel className="order-1 md:w-full" htmlFor="profile-email" isRequired>
            {t('profile.contact.emailLabel')}
          </FieldLabel>
          <VerificationBadge className="order-2 shrink-0 md:order-3" isVerified={requirements.EMAIL} />
          <Input
            className="order-3 w-full md:order-2 md:w-auto md:max-w-sm md:flex-1"
            disabled
            id="profile-email"
            value={email}
          />
          <p className="order-4 w-full text-xs text-muted-foreground">{t('profile.contact.emailNote')}</p>
        </div>

        <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
          <FieldLabel className="order-1 md:w-full" htmlFor="profile-phone" isRequired>
            {t('profile.contact.phoneLabel')}
          </FieldLabel>
          <VerificationBadge className="order-2 shrink-0 md:order-3" isVerified={phoneBadge} />
          <Controller
            control={control}
            name="phone"
            render={({ field }) => (
              <PhoneInput
                aria-invalid={Boolean(fieldErrorKey)}
                className={cn('order-3 w-full md:order-2 md:w-auto md:max-w-sm md:flex-1', {
                  '[&_button]:border-destructive [&_input]:border-destructive': Boolean(fieldErrorKey),
                })}
                countryIndicator="letters"
                defaultCountry="MX"
                id="profile-phone"
                international
                onBlur={field.onBlur}
                onChange={(value) => {
                  if (fieldErrorKey) clearErrors('phone');
                  field.onChange(value ?? '');
                }}
                placeholder={t('phone.phonePlaceholder')}
                value={field.value}
              />
            )}
          />
          {fieldErrorKey ? (
            <p className="order-4 w-full text-xs text-destructive">{t(fieldErrorKey)}</p>
          ) : (
            <p className="order-4 w-full text-xs text-muted-foreground">{t('profile.contact.phoneNote')}</p>
          )}
        </div>
      </Card>

      {children}

      <div className="flex flex-col gap-4 font-sans sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-foreground">
          <Trans components={{ strong: <strong /> }} i18nKey="identity:profile.contact.footnote" />
        </p>
        <Button className="w-full sm:w-fit" disabled={isSubmitDisabled} type="submit">
          {t('profile.contact.submit')}
        </Button>
      </div>
    </form>
  );
};
